import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PAYWALL_EVENT, getToken } from '../api';
import { logAnalyticsEvent } from '../auth/analytics';
import { qk } from '../data/keys';
import { useMe, useSetDevTier } from '../data/queries';
import type { MeResponse, SubscriptionTier } from '../data/dto';
import { billingMode, paddleMonthlyPriceIds, paddleLifetimePriceIds } from './billingMode';
import { getPaddle, PADDLE_COMPLETE_EVENT } from './paddle';
import { PaywallSheet } from '../components/upgrade/PaywallSheet';

/** Why the paywall opened — drives the headline copy. Null = generic upgrade. */
export interface PaywallReason {
  code?: string;
  feature?: string;
  limit?: number;
  current?: number;
  tier?: SubscriptionTier;
}

/** Checkout options. `lifetime` buys the one-time Full Journey pass for the tier. */
export interface CheckoutOpts {
  lifetime?: boolean;
}

interface BillingContextValue {
  /** Open the paywall, optionally with the gating reason (feature/limit). */
  openPaywall: (reason?: PaywallReason | null) => void;
  closePaywall: () => void;
  /** Begin checkout for a paid tier (mock → dev grant; paddle → overlay). */
  openCheckout: (tier: SubscriptionTier, opts?: CheckoutOpts) => Promise<void>;
  checkoutPending: boolean;
  mode: typeof billingMode;
}

const BillingContext = createContext<BillingContextValue | null>(null);

export function BillingProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const setDevTier = useSetDevTier();
  // Only fetch /me once we hold a token (avoids a 401 storm before sign-in).
  const me = useMe(!!getToken());

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<PaywallReason | null>(null);

  const openPaywall = useCallback((r?: PaywallReason | null) => {
    setReason(r ?? null);
    setOpen(true);
    logAnalyticsEvent('view_paywall', {
      code: r?.code,
      feature: r?.feature,
      required_tier: r?.tier,
    });
  }, []);
  const closePaywall = useCallback(() => setOpen(false), []);

  // Global 402 → open the targeted paywall (dispatched from api.ts).
  useEffect(() => {
    const onPaywall = (e: Event) => {
      const detail = (e as CustomEvent).detail as PaywallReason | undefined;
      openPaywall(detail ?? null);
    };
    const onComplete = () => {
      qc.invalidateQueries({ queryKey: qk.me() });
      qc.invalidateQueries({ queryKey: qk.babies() });
      setOpen(false);
      logAnalyticsEvent('purchase_complete', { mode: 'paddle' });
    };
    window.addEventListener(PAYWALL_EVENT, onPaywall);
    window.addEventListener(PADDLE_COMPLETE_EVENT, onComplete);
    return () => {
      window.removeEventListener(PAYWALL_EVENT, onPaywall);
      window.removeEventListener(PADDLE_COMPLETE_EVENT, onComplete);
    };
  }, [openPaywall, qc]);

  const openCheckout = useCallback(
    async (tier: SubscriptionTier, opts?: CheckoutOpts) => {
      if (tier === 'free') return;
      const lifetime = !!opts?.lifetime;
      logAnalyticsEvent('begin_checkout', { tier, lifetime, mode: billingMode });

      if (billingMode === 'mock') {
        await setDevTier.mutateAsync({ tier, lifetime });
        setOpen(false);
        return;
      }

      // Paddle overlay. customData carries the RevenueCat App User ID (= our
      // user id) so RevenueCat attributes the purchase to the right account.
      const userId = (qc.getQueryData<MeResponse>(qk.me()) ?? me.data)?.user.id;
      const key = tier as 'premium' | 'ultra';
      const priceId = lifetime ? paddleLifetimePriceIds[key] : paddleMonthlyPriceIds[key];
      if (!priceId) throw new Error(`No Paddle price configured for ${tier}${lifetime ? ' (lifetime)' : ''}.`);
      const paddle = await getPaddle();
      paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customData: userId ? { rc_app_user_id: userId } : undefined,
      });
    },
    [setDevTier, qc, me.data],
  );

  const value = useMemo<BillingContextValue>(
    () => ({
      openPaywall,
      closePaywall,
      openCheckout,
      checkoutPending: setDevTier.isPending,
      mode: billingMode,
    }),
    [openPaywall, closePaywall, openCheckout, setDevTier.isPending],
  );

  return (
    <BillingContext.Provider value={value}>
      {children}
      <PaywallSheet open={open} onClose={closePaywall} reason={reason} />
    </BillingContext.Provider>
  );
}

const DEFAULT_BILLING_VALUE: BillingContextValue = {
  openPaywall: () => {},
  closePaywall: () => {},
  openCheckout: async () => {},
  checkoutPending: false,
  mode: 'mock',
};

export function useBilling(): BillingContextValue {
  const ctx = useContext(BillingContext);
  if (!ctx) {
    if (import.meta.env.DEV) {
      console.warn('useBilling called outside <BillingProvider>; using default fallback');
    }
    return DEFAULT_BILLING_VALUE;
  }
  return ctx;
}
